<div *ngIf="!emptyProject || featureDisabled">
  <div class="page-header clearfix">
    <h1 class="page-header__title">Performance</h1>
    <ab-time-dur-switch
      class="page-header__element"
      [enabled]="timeDurSwitchEnabled"
      [dur]="dur"
      [lastN]="lastN"
      (changed)="onTimeDurSwitchChange($event)"
    ></ab-time-dur-switch>
    <ab-request-method-switch
      class="page-header__element"
      [featureDisabled]="featureDisabled"
      (changed)="onRequestMethodChange($event)"
      (deleted)="onRequestMethodDelete()"
      [requestMethod]="requestMethod"
    ></ab-request-method-switch>
    <ab-environment-switch
      class="page-header__element"
      [featureDisabled]="featureDisabled"
      [project]="project"
      (changed)="onEnvironmentChange($event)"
    ></ab-environment-switch>
  </div>
</div>
<div class="alerts-container">
  <ab-alerts-common></ab-alerts-common>
  <ab-account-apm-cap-reached [user]="user"></ab-account-apm-cap-reached>
</div>
<div
  class="empty-state col-xs-12"
  *ngIf="emptyProject && featureDisabled === false"
>
  <div class="empty-state-header lp-gradient-bkg">
    <div class="empty-state-header__content" *ngIf="installedNotifier === false">
      <h2>Application Performance Monitoring</h2>
      <p class="mt-4">
        For Rails apps, all that is needed to get started is to install the
        current version of our gem and make sure the
        <code>performance_stats</code> option is set to <code>true</code> in
        your Airbrake config file.
      </p>
      <a class="btn btn--success btn--default mt-4" uiSref="project-errors">
        Install Airbrake
      </a>
    </div>
    <div
      class="empty-state-header__content"
      *ngIf="installedNotifier"
      [ngSwitch]="notifierLanguage"
    >
      <h2>Application Performance Monitoring</h2>
      <div *ngSwitchCase="'ruby'">
        <p>Update your gems:</p>
        <pre>
bundle add airbrake
bundle update airbrake airbrake-ruby
</pre
        >
        <p>Update <code>config/initializers/airbrake.rb</code>:</p>
        <pre>
Airbrake.configure do |c|
  # Add the following line to your config:
  c.performance_stats = true
end</pre
        >
      </div>
      <div *ngSwitchCase="'go'">
        <p>
          In order to collect some basic routes stats you can instrument your
          application using <code>notifier.Routes.Notify</code> API.
        </p>
        <p>
          We also have prepared HTTP middleware examples in our official GitHub
          repo:
        </p>
        <a
          class="btn btn--primary btn--default external-link"
          href="https://github.com/airbrake/gobrake#sending-routes-stats"
          target="_blank"
        >
          Check out how to send route stats
        </a>
      </div>
      <div *ngSwitchCase="'python'">
        <p>
          Performance Monitoring is supported out of the box - no extra setup
          required. All you have to do is install the latest version of the
          library.
        </p>
        <p>
          <a
            class="btn btn--primary btn--default external-link"
            href="https://github.com/airbrake/pybrake#django-integration"
            target="_blank"
          >
            View Django documentation
          </a>
        </p>
        <p>
          <a
            class="btn btn--primary btn--default external-link"
            href="https://github.com/airbrake/pybrake#flask-integration"
            target="_blank"
          >
            View Flask documentation
          </a>
        </p>
      </div>
      <div *ngSwitchCase="'django'">
        <p>
          Performance Monitoring is supported out of the box. You just need the
          latest Python notifier:
        </p>
        <a
          class="btn btn--primary btn--default external-link"
          href="https://github.com/airbrake/pybrake#django-integration"
          target="_blank"
        >
          Update your Python notifier
        </a>
      </div>
      <div *ngSwitchCase="'nodejs'">
        <p>
          Performance Monitoring for Node.js projects currently supports the
          Express.js framework.
        </p>
        <h3>Update your notifier:</h3>
        <pre>
npm update @airbrake/node
// or
yarn upgrade @airbrake/node</pre
        >
        <h3>Include Express.js middleware and add it to your app:</h3>
        <pre>
const airbrakeExpress = require('@airbrake/node/dist/instrumentation/express');
// This middleware should be added before any routes are defined:
app.use(airbrakeExpress.makeMiddleware(airbrake));</pre
        >
        <p>
          For more detailed information check out
          <ab-external-link
            href="https://github.com/airbrake/airbrake-js/blob/master/packages/node/examples/express/app.js"
          >
            our full example Express.js app on GitHub </ab-external-link
          >.
        </p>
      </div>
      <div *ngSwitchDefault>
        <p class="mt-4">
          Your notifier isn't officially supported yet. If you are interested in
          this feature, please let us know below:
        </p>
        <a
          class="btn btn--primary btn--default"
          href="mailto:support@airbrake.io?subject=APM feedback"
        >
          Request Performance Monitoring for {{ notifierName }}
        </a>
      </div>
    </div>
    <img
      class="empty-state-header__img"
      src="{{ appEnv.cdnUrl }}/{{
        appEnv.assetsPath
      }}/images/performance-graph.svg"
    />
  </div>
  <div class="container">
    <div class="row">
      <div class="col-md-12">
        <div class="hexagon-bkg">
          <h1 class="mt-4 text-center">Features</h1>
          <div class="hexagon-bkg__icon"></div>
        </div>
      </div>
      <div class="col-md-6">
        <div class="overlapped">
          <img
            class="overlapped__img"
            src="{{ appEnv.cdnUrl }}/{{
              appEnv.assetsPath
            }}/images/apm-feature-01.png"
            srcset="
              {{appEnv.cdnUrl}}/{{appEnv.assetsPath}}/images/apm-feature-01.png    1x,
              {{appEnv.cdnUrl}}/{{appEnv.assetsPath}}/images/apm-feature-01@2x.png 2x
            "
          />
          <img
            class="overlapped__img"
            src="{{ appEnv.cdnUrl }}/{{
              appEnv.assetsPath
            }}/images/apm-feature-02.png"
            srcset="
              {{appEnv.cdnUrl}}/{{appEnv.assetsPath}}/images/apm-feature-02.png    1x,
              {{appEnv.cdnUrl}}/{{appEnv.assetsPath}}/images/apm-feature-02@2x.png 2x
            "
          />
        </div>
        <div class="row">
          <div class="col-md-6">
            <h3 class="mt-4">Performance Dashboard</h3>
            <p>
              Your control center, designed to give you a quick overview of your
              application performance. Requests, response times, failure rates –
              both now and over time. And of course, errors. Because, again, you
              don’t just want to know that there’s a performance problem. You
              want to know who’s affected and how to fix it.
            </p>
          </div>
          <div class="col-md-6">
            <h3 class="mt-4">Tracking user satisfaction with Apdex</h3>
            <p>
              Your application's performance measured in a way that your whole
              team can understand, allowing everyone to monitor and improve
              customer satisfaction.
            </p>
          </div>
        </div>
      </div>
      <div class="col-md-6">
        <div class="overlapped overlapped--inverted">
          <img
            class="overlapped__img"
            src="{{ appEnv.cdnUrl }}/{{
              appEnv.assetsPath
            }}/images/apm-feature-03.png"
            srcset="
              {{appEnv.cdnUrl}}/{{appEnv.assetsPath}}/images/apm-feature-03.png    1x,
              {{appEnv.cdnUrl}}/{{appEnv.assetsPath}}/images/apm-feature-03@2x.png 2x
            "
          />
          <img
            class="overlapped__img"
            src="{{ appEnv.cdnUrl }}/{{
              appEnv.assetsPath
            }}/images/apm-feature-04.png"
            srcset="
              {{appEnv.cdnUrl}}/{{appEnv.assetsPath}}/images/apm-feature-04.png    1x,
              {{appEnv.cdnUrl}}/{{appEnv.assetsPath}}/images/apm-feature-04@2x.png 2x
            "
          />
        </div>
        <div class="row">
          <div class="col-md-6">
            <h3 class="mt-4">Performance Breakdown</h3>
            <p>
              The Performance Breakdown is a summary of response timing across
              all the monitored requests for a specific route. It shows you how
              long each part of the request took (fun fact: this was most
              requested feature in the private Performance Monitoring beta).
              This helps to show you what the biggest factor is in the response
              time of a given route.
            </p>
          </div>
          <div class="col-md-6">
            <h3 class="mt-4">Digging deeper into databases</h3>
            <p>
              Do you see a big part of a slow route is being spent on database
              time? Query Analysis helps you understand why so you can fix it by
              directing you to the exact query that’s slowing things down.
            </p>
          </div>
        </div>
      </div>
      <div class="col-md-12">
        <h3 class="mt-4">Supported languages</h3>
        <p>
          Performance Monitoring is currently available for
          <ab-external-link
            href="https://docs.airbrake.io/docs/overview/apm/#monitoring-rails-apps"
          >
            Rails </ab-external-link
          >,
          <ab-external-link
            href="https://docs.airbrake.io/docs/overview/apm/#monitoring-go-apps"
          >
            Go </ab-external-link
          >,
          <ab-external-link
            href="https://docs.airbrake.io/docs/overview/apm/#monitoring-nodejs-apps"
          >
            Node.js </ab-external-link
          >,
          <ab-external-link
            href="https://docs.airbrake.io/docs/overview/apm/#monitoring-django-apps"
          >
            Django </ab-external-link
          >, and
          <ab-external-link
            href="https://docs.airbrake.io/docs/overview/apm/#monitoring-flask-apps"
          >
            Flask
          </ab-external-link>
          projects. Want Performance Monitoring support added for your language?
          <button
            class="btn btn--link btn--no-shadow"
            (click)="performanceFeedback()"
          >
            Let us know</button
          >.
        </p>
      </div>
    </div>
  </div>
</div>
<div
  class="flex-grow-1 content-mask-container"
  *ngIf="!emptyProject || featureDisabled"
>
  <div class="content-mask" *ngIf="featureDisabled">
    <div class="content-mask__content">
      <p class="content-mask__text">
        This Dashboard is showing sample data. Enable the Performance Dashboard
        to see an overview of your project with detailed performance analytics.
      </p>
      <button
        class="btn btn--upgrade"
        (click)="openUpgradeModal($event)"
        data-feature="performance_dashboard"
        type="button"
      >
        Enable Performance Dashboard
      </button>
    </div>
  </div>
  <ab-route-table
    *ngIf="showRouteTable"
    [project]="project"
    [dur]="dur"
    [lastN]="lastN"
    [timeDurAgo]="timeDurAgo"
    [httpMethod]="requestMethod"
    (loadingChanged)="onLoading($event)"
    [environment]="environment"
    [featureDisabled]="featureDisabled"
    [currentTab]="currentTab"
    [queryPage]="queryPage"
    [routeSearch]="routeSearch"
    [querySearch]="querySearch"
    [sortQueryColumn]="sortQueryColumn"
    [sortQueryDesc]="sortQueryDesc"
    [queuePage]="queuePage"
    [sortQueueColumn]="sortQueueColumn"
    [sortQueueDesc]="sortQueueDesc"
    [queueSearch]="queueSearch"
  >
  </ab-route-table>
</div>
